import { Injectable, Injector } from '@angular/core';
import { environment } from 'src/environments/environment';
import { NotifierEvents } from '../common/enums/enums';
import { CustomError } from '../common/helpers/custom-error';
import { GuidGenerator } from '../common/helpers/guid-generator';
import { FamilyProjectBaseModel } from '../common/models/familyproject/family-project-base-model';
import { UserModel } from '../common/models/UserModel';
import { FamilyProjectApiService } from './API/family-project-api.service';
import { IndividualApiService } from './API/individual-api.service';
import { AuthenticationService } from './authentication.service';
import { HistoryService } from './history.service';
import { NotifierV2Service } from './notifier-v2.service';
import { PreferencesService } from './preferences.service';
import { LoadingService } from './UI/loading.service';
import { IndividualIdbService  } from './IndexedDb/individual-idb.service';
import { TreeApiService } from './API/tree-api.service';
import { ProjectIdbService } from './IndexedDb/project-idb.service';

@Injectable()

// Project references will be maintained here/ project list, current project & project token for intercept headers
export class ProjectRefService {
    projectList         : FamilyProjectBaseModel[] = [];
    defaultProject      : FamilyProjectBaseModel;
    currentProjectToken : string = null;
    appDefaultCulture   : string;
    isIndexedDB         : boolean;

    // Lazy-loaded to avoid circular dependency:
    // HTTP_INTERCEPTORS -> TokenInterceptorService -> ProjectRefService -> NotifierV2Service -> HttpClient -> HTTP_INTERCEPTORS
    private _notifierService: NotifierV2Service;
    private get notifierService(): NotifierV2Service {
        if (!this._notifierService) {
            this._notifierService = this.injector.get(NotifierV2Service);
        }
        return this._notifierService;
    }
    
    constructor(private authenticationService   : AuthenticationService, 
                private familyProjectService    : FamilyProjectApiService,
                private preferenceService       : PreferencesService,
                private individualService       : IndividualApiService,
                private injector                : Injector,
                private loadingService          : LoadingService,
                private historyService          : HistoryService,
                private individualDbService     : IndividualIdbService ,
                private projectDbService     : ProjectIdbService ,
                private treeService     : TreeApiService) {
        
        this.appDefaultCulture = environment.DEFAULT_CULTURE;
        this.isIndexedDB = environment.IS_INDEXED_DB_ENABLED;
        
    }

    // Load/Reload project list
    async load(): Promise<void> {
      this.historyService.clear();
  
      try {
  
        const projectData = await this.familyProjectService
          .getFamilyProjects<any>()
          .toPromise();
        if (!projectData?.data?.length) {
          // Route to project create
          return;
        }
        this.projectList = (projectData?.data ?? []) as FamilyProjectBaseModel[];
  
        this.defaultProject = this.projectList.find((p) => p.isDefault) || null;
  
        const defaultProjectId = this.defaultProject
          ? this.defaultProject.familyProjectId
          : "";
  
        await this.setCurrentProject(defaultProjectId);
  
      } catch (error) {
        // Handle and log any errors that occur during project loading
        console.error("Error loading projects:", error);
      }
    }

    public async setCurrentProject(id){
        this.historyService.clear() // 2022.02.03 added due to fix a breadcrumb issue when project changes
        let processId = GuidGenerator.generate();
        try {            
            this.loadingService.show(processId);

            this.defaultProject = this.projectList.find( p=> p.familyProjectId == id );
            // if no default set the first project as default
            if ( !this.defaultProject ||  this.defaultProject == null ){
                this.defaultProject = this.projectList[0];
            } 
            this.setCurrentProjectToken(); 
            
            // set preferences
            this.preferenceService.setPreferences(this.defaultProject?.projectPreferences);
            this.preferenceService.setCulture(this.appDefaultCulture);
    
            if ( !this.defaultProject?.isValid) return;
            
            if(this.isIndexedDB){
            try {
                const tableName = await this.individualDbService.getTableName(id);
                const isExist = await this.individualDbService.checkTableExists(tableName);
                if (!isExist) {
                  const treeNodes = await this.treeService.getTreeNodesByProject<any>().toPromise();
                  await this.individualDbService.createTable(tableName);
                  await this.individualDbService.saveData(id, treeNodes.data);
                } else {
                  console.log(`Data already exists in the database. Skipping API call.`);
                }
                await this.projectDbService.setProject(id);
              } 
              catch (error) {
                console.error(`Error during IndexedDB operation (exists/insert):`, error);
              }
            }
              
            /** --------------------------------- Load the root person ------------------------------------ */
            // Get the first person in the project to make as root person    
            var rootPersonData = await this.individualService.getFirstPerson<any>().toPromise().catch( ()=>{
            
            });
              
            // Update root member changed
            this.notifierService.updateEvent(NotifierEvents.RootMemberChanged, {
                Id          : rootPersonData.data.id,
                DisplayName : rootPersonData.data.displayName,
                FirstName   : rootPersonData.data.firstName,
                LastName    : rootPersonData.data.lastName
            });    
        } catch (error) {
            throw new CustomError("projectRefServices => setCurrentProject() : " + error, 911, false);
        }finally{
            this.loadingService.hide(processId);
        }        
    }

    private setCurrentProjectToken(){
        if ( !this.defaultProject || this.defaultProject == null ){
            return null;
        }
        
        let onlineId = "";        
        let user:UserModel = this.authenticationService.getUser();
        if ( user ){
            onlineId = user.OnlineId;
        }
        let currentProjectJson = {    'ProjectId' : this.defaultProject.familyProjectId,
                                      'OnlineId' : onlineId
                                 }
        this.currentProjectToken = btoa( encodeURIComponent(JSON.stringify(currentProjectJson) ) );
    }
    
    public getProjectList():FamilyProjectBaseModel[]{
        return this.projectList;
    }

    public getCurrentProject(): FamilyProjectBaseModel {
        return this.defaultProject;
    }

    public isCurrentProjectInvalid1(): boolean {
        //return true;
        return !(this.defaultProject && this.defaultProject.isValid);
    }

    public isCurrentProjectEditable(): boolean {
        //return true;
        return (this.defaultProject && this.defaultProject.isValid && !this.defaultProject.isImporting );
    }
    public getCurrentProjectToken() : string {
       return this.currentProjectToken;
    }
}